import { differenceInYears } from 'date-fns';

import { Option } from '@storeblocks/radio';
import { CarDetails } from 'Api/insurance';
import { CarCoverage } from 'Types/coverages/coverages.types';
import { isUnlimitedYearlyKmNeedAgreement } from 'Utils/agreementCodes/agreementCodes.utils';
import { getAppText } from 'Utils/text';
import {
  isAnsvarCoverage,
  isDelkaskoCoverage,
  isKaskoCoverage,
} from '../motorcycle/helpers/changeMotorcycleForm.helpers';
import {
  ansvarCoverageCar,
  delkaskoCoverageCar,
  kaskoCoverageCar,
  superCoverageOld,
  superCoverageV2,
} from './changeCarForm.constants';
import {
  ChangeCarFormValues,
  CoverageConfig,
  CoverageOption,
  RentalType,
} from './changeCarForm.types';

export const getRentalTypeInitialValue = (
  rentalType: RentalType | null | undefined,
  coverage: CarCoverage,
): RentalType => {
  if (isOldCarSuperCoverage(coverage)) {
    return RentalType.RENTAL_TYPE_ORDINARY;
  }
  if (rentalType === null || rentalType === undefined) {
    return RentalType.RENTAL_TYPE_NON;
  }
  return rentalType;
};

export const getRentalCarIncludedInitialValue = (
  rentalType: RentalType,
  coverage: string,
): boolean => {
  if (coverage === CarCoverage.FULL_SUPER) {
    return true;
  }
  if (
    rentalType === undefined ||
    rentalType === null ||
    rentalType === RentalType.RENTAL_TYPE_NON
  ) {
    return false;
  }
  return true;
};

export const canChangeYearlyKmNeed = ({
  beneficiaryMileage,
  isDeregistered,
  agreementProduct,
}: CarDetails): boolean => {
  return !(
    beneficiaryMileage ||
    isDeregistered ||
    isUnlimitedYearlyKmNeedAgreement(agreementProduct)
  );
};

export const canChangeContribution = (coverage: string): boolean => {
  return (
    coverage !== CarCoverage.REDUCED_OWN_DAMAGE &&
    coverage !== CarCoverage.ACCIDENT
  );
};

export const hasCarRiskFieldsChanged = (
  initialValues: ChangeCarFormValues,
  values: ChangeCarFormValues,
): boolean => {
  return (
    initialValues.usedByYoungerDriver !== values.usedByYoungerDriver ||
    initialValues.yearlyKmNeed !== values.yearlyKmNeed ||
    initialValues.mileage !== values.mileage
  );
};

export const rentalTypeIncludedOptions = (
  currentCoverage: CarCoverage,
  initialValues: ChangeCarFormValues,
  values: ChangeCarFormValues,
): Option[] => {
  const isNoRentalCarOptionDisabled =
    hasCarRiskFieldsChanged(initialValues, values) &&
    initialValues.rentalType !== RentalType.RENTAL_TYPE_NON;

  if (isOldCarSuperCoverage(currentCoverage)) {
    return [
      {
        id: RentalType.RENTAL_TYPE_NON,
        label: getAppText('changeInsurance.car.rentalTypeOption.label.NON'),
        value: RentalType.RENTAL_TYPE_NON,
        trackid: RentalType.RENTAL_TYPE_NON,
        disabled: isNoRentalCarOptionDisabled,
      },
      {
        id: RentalType.RENTAL_TYPE_ORDINARY,
        label: getAppText(
          'changeInsurance.car.rentalTypeOption.label.ORDINARY',
        ),
        hint: getAppText('changeInsurance.car.rentalTypeOption.hint.ORDINARY'),
        value: RentalType.RENTAL_TYPE_ORDINARY,
        trackid: RentalType.RENTAL_TYPE_ORDINARY,
      },
    ];
  }
  return [
    {
      id: RentalType.RENTAL_TYPE_NON,
      label: getAppText('changeInsurance.car.rentalTypeOption.label.NON'),
      value: RentalType.RENTAL_TYPE_NON,
      trackid: RentalType.RENTAL_TYPE_NON,
      disabled: isNoRentalCarOptionDisabled,
    },
    {
      id: RentalType.RENTAL_TYPE_ORDINARY,
      label: getAppText('changeInsurance.car.rentalTypeOption.label.ORDINARY'),
      hint: getAppText('changeInsurance.car.rentalTypeOption.hint.ORDINARY'),
      value: RentalType.RENTAL_TYPE_ORDINARY,
      trackid: RentalType.RENTAL_TYPE_ORDINARY,
    },
    {
      id: RentalType.RENTAL_TYPE_EXTENDED,
      label: getAppText('changeInsurance.car.rentalTypeOption.label.EXTENDED'),
      hint: getAppText('changeInsurance.car.rentalTypeOption.hint.EXTENDED'),
      value: RentalType.RENTAL_TYPE_EXTENDED,
      trackid: RentalType.RENTAL_TYPE_EXTENDED,
    },
  ];
};

export const isSuperOrFullOwnDamageSelected = (
  coverage: CarCoverage,
): boolean => {
  return (
    coverage === CarCoverage.FULL_SUPER ||
    coverage === CarCoverage.FULL_SUPER_V2 ||
    coverage === CarCoverage.FULL_OWN_DAMAGE ||
    coverage === CarCoverage.FULL_OWN_DAMAGE_INCLUDING_RENTALCAR
  );
};

export const getRentalCarIncluded = (
  rentalType: RentalType,
  coverage: CarCoverage,
): boolean | undefined => {
  if (!isSuperOrFullOwnDamageSelected(coverage)) {
    return undefined;
  }
  if (rentalType === RentalType.RENTAL_TYPE_NON) {
    return false;
  }
  return true;
};

export const getRentalType = (
  rentalType: RentalType | null,
  coverage: CarCoverage,
): RentalType | null | undefined => {
  if (isOldCarSuperCoverage(coverage)) {
    return RentalType.RENTAL_TYPE_EXTENDED;
  }
  if (!isSuperOrFullOwnDamageSelected(coverage)) {
    return undefined;
  }
  if (rentalType === RentalType.RENTAL_TYPE_NON || rentalType === null) {
    return null;
  }
  return rentalType;
};

export const isOldCarSuperCoverage = (coverage: CarCoverage): boolean =>
  coverage === CarCoverage.FULL_SUPER;

export const isNewCarSuperCoverage = (coverage: CarCoverage): boolean =>
  coverage === CarCoverage.FULL_SUPER_V2;

const COVERAGE_CONFIG = (
  initialValues: ChangeCarFormValues,
  values: ChangeCarFormValues,
): CoverageConfig => {
  const isCarRiskFieldsChanged = hasCarRiskFieldsChanged(initialValues, values);

  const disableAnsvar =
    isCarRiskFieldsChanged && !isAnsvarCoverage(initialValues.coverage);

  const disabledDelkasko =
    isCarRiskFieldsChanged && !isDelkaskoCoverage(initialValues.coverage);

  const disableKasko =
    isCarRiskFieldsChanged && !isKaskoCoverage(initialValues.coverage);

  return {
    [CarCoverage.ACCIDENT]: {
      default: [ansvarCoverageCar(disableAnsvar)],
      mortgagee: [ansvarCoverageCar(disableAnsvar)],
      notOlderThan7: [],
      olderThan7: [],
      notOlderThan10: [],
      olderThan10: [],
    },
    [CarCoverage.REDUCED_OWN_DAMAGE]: {
      default: [
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
      mortgagee: [delkaskoCoverageCar(disabledDelkasko)],
      notOlderThan7: [],
      olderThan7: [delkaskoCoverageCar(disabledDelkasko)],
      notOlderThan10: [delkaskoCoverageCar(disabledDelkasko)],
      olderThan10: [delkaskoCoverageCar(disabledDelkasko)],
    },
    [CarCoverage.FULL_OWN_DAMAGE]: {
      default: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
      mortgagee: [kaskoCoverageCar(disableKasko)],
      notOlderThan7: [kaskoCoverageCar(disableKasko)],
      olderThan7: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      notOlderThan10: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      olderThan10: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
    },
    [CarCoverage.FULL_OWN_DAMAGE_INCLUDING_RENTALCAR]: {
      default: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
      mortgagee: [kaskoCoverageCar(disableKasko)],
      notOlderThan7: [kaskoCoverageCar(disableKasko)],
      olderThan7: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      notOlderThan10: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      olderThan10: [
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
    },
    [CarCoverage.FULL_SUPER]: {
      default: [
        superCoverageOld,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
      mortgagee: [superCoverageOld, kaskoCoverageCar(disableKasko)],
      notOlderThan7: [superCoverageOld, kaskoCoverageCar(disableKasko)],
      olderThan7: [
        superCoverageOld,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      notOlderThan10: [
        superCoverageOld,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      olderThan10: [
        superCoverageOld,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
    },
    [CarCoverage.FULL_SUPER_V2]: {
      default: [
        superCoverageV2,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
        ansvarCoverageCar(disableAnsvar),
      ],
      mortgagee: [superCoverageV2, kaskoCoverageCar(disableKasko)],
      notOlderThan7: [superCoverageV2, kaskoCoverageCar(disableKasko)],
      olderThan7: [
        superCoverageV2,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      notOlderThan10: [
        superCoverageV2,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
      olderThan10: [
        superCoverageV2,
        kaskoCoverageCar(disableKasko),
        delkaskoCoverageCar(disabledDelkasko),
      ],
    },
  };
};

export const getDeregisteredCoverageOptions = (
  coverageOptions: CoverageOption[],
  dateOfFirstRegistration: string,
  hasMortgagee: boolean,
): CoverageOption[] => {
  let deregisteredCoverageOptions = [];

  deregisteredCoverageOptions = coverageOptions.filter(
    (coverageOption) => coverageOption.value !== CarCoverage.ACCIDENT,
  );

  if (isCarNotOlderThan7Years(dateOfFirstRegistration) || hasMortgagee) {
    deregisteredCoverageOptions = coverageOptions.filter(
      (coverageOption) =>
        coverageOption.value !== CarCoverage.ACCIDENT &&
        coverageOption.value !== CarCoverage.REDUCED_OWN_DAMAGE,
    );
  }
  return deregisteredCoverageOptions.map((coverageOption) => ({
    ...coverageOption,
    label: `${coverageOption.label} uten ansvarsforsikring`,
  }));
};

export const getDefaultCoverageOptions = (
  coverage: string,
  initialValues: ChangeCarFormValues,
  values: ChangeCarFormValues,
): CoverageOption[] => COVERAGE_CONFIG(initialValues, values)[coverage].default;

export const isCarNotOlderThan7Years = (
  dateOfFirstRegistration: string,
): boolean => !isCarOlderThanXYears(dateOfFirstRegistration, 7);

export const isCarOlderThan7Years = (
  dateOfFirstRegistration: string,
): boolean => isCarOlderThanXYears(dateOfFirstRegistration, 7);

export const isCarNotOlderThan10Years = (
  dateOfFirstRegistration: string,
): boolean => !isCarOlderThanXYears(dateOfFirstRegistration, 10);

export const isCarOlderThan10Years = (
  dateOfFirstRegistration: string,
): boolean => isCarOlderThanXYears(dateOfFirstRegistration, 10);

export const isCarOlderThanXYears = (
  registrationDate: string,
  years: number,
): boolean => {
  const [day, month, year] = registrationDate.split('.');
  const yearsDifference = differenceInYears(
    new Date(),
    new Date(Number(year), Number(month), Number(day)),
  );

  return yearsDifference > years;
};

export const getCarCoverageOptions = (
  insuranceDetails: CarDetails,
  initialValues: ChangeCarFormValues,
  values: ChangeCarFormValues,
): CoverageOption[] => {
  const { coverage, hasMortgagee, isDeregistered, dateOfFirstRegistration } =
    insuranceDetails;

  if (!(coverage in COVERAGE_CONFIG(initialValues, values))) {
    return [];
  }
  const defaultCoverageOptions = getDefaultCoverageOptions(
    coverage as CarCoverage,
    initialValues,
    values,
  );

  if (hasMortgagee) {
    return COVERAGE_CONFIG(initialValues, values)[coverage].mortgagee;
  }
  if (!isDeregistered) {
    if (isCarOlderThan10Years(dateOfFirstRegistration)) {
      return COVERAGE_CONFIG(initialValues, values)[coverage].olderThan10;
    }
    if (isCarOlderThan7Years(dateOfFirstRegistration)) {
      return COVERAGE_CONFIG(initialValues, values)[coverage].olderThan7;
    }
    if (isCarNotOlderThan7Years(dateOfFirstRegistration)) {
      return COVERAGE_CONFIG(initialValues, values)[coverage].notOlderThan7;
    }
    if (isCarNotOlderThan10Years(dateOfFirstRegistration)) {
      return COVERAGE_CONFIG(initialValues, values)[coverage].notOlderThan10;
    }
  }

  if (isDeregistered) {
    return getDeregisteredCoverageOptions(
      defaultCoverageOptions,
      dateOfFirstRegistration,
      hasMortgagee,
    );
  }
  return defaultCoverageOptions;
};

export const isDowngradingCarCoverage = (
  initialCoverage: string,
  newCoverage: string,
): boolean =>
  isDowngradingFromSuper(initialCoverage, newCoverage) ||
  isDowngradingFromKasko(initialCoverage, newCoverage) ||
  isDowngradingFromDelkasko(initialCoverage, newCoverage);

export const isUpgradingCarCoverage = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    isUpgradingFromAnsvar(initialCoverage, newCoverage) ||
    isUpgradingFromDelkasko(initialCoverage, newCoverage) ||
    isUpgradingFromKasko(initialCoverage, newCoverage)
  );
};

const isUpgradingFromAnsvar = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    initialCoverage === CarCoverage.ACCIDENT &&
    (newCoverage === CarCoverage.REDUCED_OWN_DAMAGE ||
      newCoverage === CarCoverage.FULL_OWN_DAMAGE ||
      newCoverage === CarCoverage.FULL_SUPER ||
      newCoverage === CarCoverage.FULL_SUPER_V2)
  );
};

const isUpgradingFromDelkasko = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    initialCoverage === CarCoverage.REDUCED_OWN_DAMAGE &&
    (newCoverage === CarCoverage.FULL_OWN_DAMAGE ||
      newCoverage === CarCoverage.FULL_SUPER ||
      newCoverage === CarCoverage.FULL_SUPER_V2)
  );
};

const isUpgradingFromKasko = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    initialCoverage === CarCoverage.FULL_OWN_DAMAGE &&
    (newCoverage === CarCoverage.FULL_SUPER ||
      newCoverage === CarCoverage.FULL_SUPER_V2)
  );
};

const isDowngradingFromSuper = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    (initialCoverage === CarCoverage.FULL_SUPER ||
      initialCoverage === CarCoverage.FULL_SUPER_V2) &&
    newCoverage !== CarCoverage.FULL_SUPER &&
    newCoverage !== CarCoverage.FULL_SUPER_V2
  );
};

const isDowngradingFromKasko = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    initialCoverage === CarCoverage.FULL_OWN_DAMAGE &&
    newCoverage !== CarCoverage.FULL_OWN_DAMAGE &&
    newCoverage !== CarCoverage.FULL_SUPER &&
    newCoverage !== CarCoverage.FULL_SUPER_V2
  );
};

const isDowngradingFromDelkasko = (
  initialCoverage: string,
  newCoverage: string,
): boolean => {
  return (
    initialCoverage === CarCoverage.REDUCED_OWN_DAMAGE &&
    newCoverage === CarCoverage.ACCIDENT
  );
};

export const isUpgradingRentalCar = (
  initialRentalType: RentalType,
  newRentalType: RentalType,
): boolean => {
  return (
    (initialRentalType === RentalType.RENTAL_TYPE_NON &&
      newRentalType !== RentalType.RENTAL_TYPE_NON) ||
    (initialRentalType === RentalType.RENTAL_TYPE_ORDINARY &&
      newRentalType === RentalType.RENTAL_TYPE_EXTENDED) ||
    (initialRentalType === RentalType.RENTAL_TYPE_NON &&
      newRentalType === RentalType.RENTAL_TYPE_EXTENDED)
  );
};

export const isDowngradingRentalCar = (
  initialRentalType: RentalType,
  newRentalType: RentalType,
): boolean => {
  return (
    (initialRentalType !== RentalType.RENTAL_TYPE_NON &&
      newRentalType === RentalType.RENTAL_TYPE_NON) ||
    (initialRentalType === RentalType.RENTAL_TYPE_EXTENDED &&
      newRentalType === RentalType.RENTAL_TYPE_ORDINARY) ||
    (initialRentalType === RentalType.RENTAL_TYPE_EXTENDED &&
      newRentalType === RentalType.RENTAL_TYPE_NON)
  );
};

export const isDowngradingToNoRentalCar = (
  initialRentalType: RentalType,
  newRentalType: RentalType,
): boolean => {
  return (
    (initialRentalType === RentalType.RENTAL_TYPE_ORDINARY &&
      newRentalType === RentalType.RENTAL_TYPE_NON) ||
    (initialRentalType === RentalType.RENTAL_TYPE_EXTENDED &&
      newRentalType === RentalType.RENTAL_TYPE_NON)
  );
};

export const isInRenewalAndConvertedFromOldSuperToNew = (
  todaysCoverage: CarCoverage,
  newCoverage: CarCoverage,
  isInRenewal: boolean,
): boolean => {
  return (
    isInRenewal &&
    todaysCoverage === CarCoverage.FULL_SUPER &&
    newCoverage === CarCoverage.FULL_SUPER_V2
  );
};
